import { ERC20 } from "@silo/typechain";
import { BigNumber, ContractReceipt, Signer, ethers } from "ethers";
import { ERC20_ABI, OnCreateTx, getGasOverride } from "@silo/contract-utils";
import { MaxUint256 } from "@ethersproject/constants";
import { noop } from "@silo/utils";
import { constNull, flow, pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";
import { ChecksumAddress, ClientProvider } from "@silo/types";

const APPROVAL_GAS_BUFFER = 10;

type Approve = (params: {
  clientProvider: ClientProvider;
  signer: Signer;
  tokenAddress: ChecksumAddress;
  spender: ChecksumAddress;
  amountToApprove?: bigint;
  onCreateTx?: OnCreateTx;
}) => Promise<ContractReceipt>;

export const approve: Approve = async ({
  clientProvider,
  signer,
  tokenAddress,
  spender,
  amountToApprove = MaxUint256.toBigInt(),
  onCreateTx = noop,
}) => {
  const erc20Contract = <ERC20>(
    new ethers.Contract(tokenAddress, ERC20_ABI, signer)
  );

  const populatedTx = await erc20Contract.populateTransaction.approve(
    spender,
    amountToApprove
  );
  const gasOverride = await getGasOverride(
    clientProvider,
    populatedTx,
    APPROVAL_GAS_BUFFER
  );

  const tx = await erc20Contract.approve(spender, amountToApprove, gasOverride);
  onCreateTx(tx);

  const receipt = await tx.wait();

  if (receipt.status === 0) {
    // see https://docs.ethers.org/v5/api/providers/types/#providers-TransactionReceipt
    throw new Error(`Transaction ${tx.hash} reverted`);
  }

  return receipt;
};

/**
 * Parses allowance the user set from a contract receipt
 */
export const parseAllowance = (receipt: ContractReceipt): bigint | null =>
  pipe(
    receipt.events,
    option.fromNullable,
    option.chain(flow(array.findFirst(({ event }) => event === "Approval"))),
    option.map(({ data }) => data),
    option.map(BigNumber.from),
    option.fold(constNull, (allowance) => allowance.toBigInt())
  );
